import { supabase } from '../providers/supabase/client';

export const DEFAULT_EXCHANGE_RATE = 3.39;

export const getExchangeRate = async () => {
    const { data, error } = await supabase
        .from('app_settings')
        .select('setting_value')
        .eq('setting_key', 'exchange_rate')
        .single();
    
    if (error) {
        console.error('Error fetching exchange rate:', error);
        return DEFAULT_EXCHANGE_RATE; // Fallback
    }
    return parseFloat(data.setting_value) || DEFAULT_EXCHANGE_RATE;
};

export const updateExchangeRate = async (newRate) => {
    const { data, error } = await supabase
        .from('app_settings')
        .upsert({
            setting_key: 'exchange_rate',
            setting_value: newRate.toString()
        })
        .select()
        .single();
    
    if (error) {
        throw error;
    }
    return parseFloat(data.setting_value);
};

/**
 * Obtiene las tasas de comisión de Culqi y el recargo por defecto
 */
export const getCulqiRates = async () => {
    const { data, error } = await supabase
        .from('app_settings')
        .select('setting_key, setting_value')
        .in('setting_key', ['culqi_pos_rate', 'culqi_link_rate', 'culqi_default_surcharge']);
    
    if (error) {
        console.error('Error fetching Culqi rates:', error);
        return { posRate: 0.0299, linkRate: 0.0344, defaultSurcharge: 0.04 };
    }

    const map = {};
    (data || []).forEach(d => { map[d.setting_key] = parseFloat(d.setting_value); });
    return {
        posRate: map['culqi_pos_rate'] || 0.0299,
        linkRate: map['culqi_link_rate'] || 0.0344,
        defaultSurcharge: map['culqi_default_surcharge'] || 0.04,
    };
};

export const getBirthdayDiscount = async () => {
    const { data, error } = await supabase
        .from('app_settings')
        .select('setting_value')
        .eq('setting_key', 'birthday_discount')
        .single();
    
    if (error) {
        return 50.00; // Fallback default $50.00
    }
    return parseFloat(data.setting_value) || 50.00;
};

export const updateBirthdayDiscount = async (newDiscount) => {
    const { data, error } = await supabase
        .from('app_settings')
        .upsert({
            setting_key: 'birthday_discount',
            setting_value: newDiscount.toString()
        })
        .select()
        .single();
    
    if (error) {
        throw error;
    }
    return parseFloat(data.setting_value);
};

export const getSunatSyncEnabled = async () => {
    const { data, error } = await supabase
        .from('app_settings')
        .select('setting_value')
        .eq('setting_key', 'sunat_sync_enabled')
        .single();
    
    if (error) {
        return false; // Default false
    }
    return data.setting_value === 'true';
};

export const updateSunatSyncEnabled = async (enabled) => {
    const { data, error } = await supabase
        .from('app_settings')
        .upsert({
            setting_key: 'sunat_sync_enabled',
            setting_value: enabled ? 'true' : 'false'
        })
        .select()
        .single();
    
    if (error) {
        throw error;
    }
    return data.setting_value === 'true';
};

export const getDocumentSequences = async () => {
    const { data, error } = await supabase
        .from('document_sequences')
        .select('*')
        .in('document_type', ['factura', 'boleta']);
    
    if (error) {
        console.error('Error fetching document sequences:', error);
        return [];
    }
    return data || [];
};

export const updateDocumentSequence = async (documentType, serie, currentNumber) => {
    // We assume there's only one active sequence per document_type.
    // If it exists, update it. If not, insert it.
    const { data: existing } = await supabase
        .from('document_sequences')
        .select('id')
        .eq('document_type', documentType)
        .limit(1)
        .single();

    if (existing) {
        const { error } = await supabase
            .from('document_sequences')
            .update({ serie, current_number: currentNumber })
            .eq('id', existing.id);
        if (error) throw error;
    } else {
        const { error } = await supabase
            .from('document_sequences')
            .insert([{ document_type: documentType, serie, current_number: currentNumber }]);
        if (error) throw error;
    }
    return true;
};

export const getSunatCredentials = async () => {
    const [userRes, passRes] = await Promise.all([
        supabase.from('app_settings').select('setting_value').eq('setting_key', 'sunat_sol_user').maybeSingle(),
        supabase.from('app_settings').select('setting_value').eq('setting_key', 'sunat_sol_pass').maybeSingle()
    ]);
    return {
        sunatUser: userRes?.data?.setting_value || '',
        sunatPass: passRes?.data?.setting_value || ''
    };
};

export const updateSunatCredentials = async (sunatUser, sunatPass) => {
    const promises = [
        supabase.from('app_settings').upsert({ setting_key: 'sunat_sol_user', setting_value: sunatUser })
    ];
    if (sunatPass) {
        promises.push(supabase.from('app_settings').upsert({ setting_key: 'sunat_sol_pass', setting_value: sunatPass }));
    }
    await Promise.all(promises);
    return true;
};

export const getPlacesWithBase64Images = async () => {
    return await supabase
        .from('places')
        .select('id, name')
        .like('image_url', 'data:%');
};

export const getPlaceImageUrl = async (id) => {
    return await supabase
        .from('places')
        .select('image_url')
        .eq('id', id)
        .single();
};

export const updatePlaceImageUrl = async (id, imageUrl) => {
    return await supabase
        .from('places')
        .update({ image_url: imageUrl })
        .eq('id', id);
};

export const getVideoMaxSize = async () => {
    const { data, error } = await supabase
        .from('app_settings')
        .select('setting_value')
        .eq('setting_key', 'hotel_video_max_size_mb')
        .maybeSingle();

    if (error || !data) {
        return 20; // Default fallback to 20MB
    }
    return parseInt(data.setting_value, 10) || 20;
};

export const updateVideoMaxSize = async (size) => {
    const { data, error } = await supabase
        .from('app_settings')
        .upsert({
            setting_key: 'hotel_video_max_size_mb',
            setting_value: size.toString()
        })
        .select()
        .single();

    if (error) {
        throw error;
    }
    return parseInt(data.setting_value, 10) || 20;
};

export const getPdfBookingMessage = async () => {
    const defaultMsg = 'No queremos presionarte, pero tampoco queremos que mañana nos escribas: "¿Y por qué subió?" y nosotros tengamos que responder con un emoji llorando. 😢🌴';
    const { data, error } = await supabase
        .from('app_settings')
        .select('setting_value')
        .eq('setting_key', 'pdf_booking_message')
        .maybeSingle();

    if (error || !data) {
        return defaultMsg;
    }
    return data.setting_value || defaultMsg;
};

export const updatePdfBookingMessage = async (message) => {
    const { data, error } = await supabase
        .from('app_settings')
        .upsert({
            setting_key: 'pdf_booking_message',
            setting_value: message
        })
        .select()
        .single();

    if (error) {
        throw error;
    }
    return data.setting_value;
};

export const DEFAULT_PDF_FONT_SIZES = {
    packages: 8.5,
    cruises: 8.5,
    hotels: 8.5,
    flights: 8.5,
};

export const DEFAULT_PDF_TITLE_FONT_SIZES = {
    packages: 9,
    cruises: 9,
    hotels: 9,
    flights: 9,
};

const PDF_FONT_SIZE_KEYS = {
    packages: 'pdf_font_size_packages',
    cruises: 'pdf_font_size_cruises',
    hotels: 'pdf_font_size_hotels',
    flights: 'pdf_font_size_flights',
};

const PDF_TITLE_FONT_SIZE_KEYS = {
    packages: 'pdf_title_font_size_packages',
    cruises: 'pdf_title_font_size_cruises',
    hotels: 'pdf_title_font_size_hotels',
    flights: 'pdf_title_font_size_flights',
};

/**
 * Obtiene de forma síncrona el tamaño de fuente guardado en caché local
 * para evitar parpadeos durante el renderizado inicial de cotizaciones.
 */
export const getCachedPdfFontSize = (type) => {
    try {
        const cached = localStorage.getItem('pdf_font_sizes');
        if (cached) {
            const parsed = JSON.parse(cached);
            if (parsed && !isNaN(parseFloat(parsed[type]))) {
                return parseFloat(parsed[type]);
            }
        }
    } catch (e) {
        // Ignorar errores de localStorage
    }
    return DEFAULT_PDF_FONT_SIZES[type] || 8.5;
};

/**
 * Obtiene de forma síncrona el tamaño de fuente de título guardado en caché local.
 */
export const getCachedPdfTitleFontSize = (type) => {
    try {
        const cached = localStorage.getItem('pdf_title_font_sizes');
        if (cached) {
            const parsed = JSON.parse(cached);
            if (parsed && !isNaN(parseFloat(parsed[type]))) {
                return parseFloat(parsed[type]);
            }
        }
    } catch (e) {}
    return DEFAULT_PDF_TITLE_FONT_SIZES[type] || 9;
};

/**
 * Obtiene todos los tamaños de fuente de las cotizaciones desde app_settings.
 */
export const getPdfFontSizes = async () => {
    try {
        const { data, error } = await supabase
            .from('app_settings')
            .select('setting_key, setting_value')
            .in('setting_key', Object.values(PDF_FONT_SIZE_KEYS));

        const result = { ...DEFAULT_PDF_FONT_SIZES };

        // Aplicar caché local como base
        try {
            const cached = localStorage.getItem('pdf_font_sizes');
            if (cached) {
                Object.assign(result, JSON.parse(cached));
            }
        } catch (e) {}

        if (!error && data) {
            data.forEach((item) => {
                const val = parseFloat(item.setting_value);
                if (!isNaN(val) && val > 0) {
                    for (const [t, key] of Object.entries(PDF_FONT_SIZE_KEYS)) {
                        if (item.setting_key === key) {
                            result[t] = val;
                        }
                    }
                }
            });
        }

        localStorage.setItem('pdf_font_sizes', JSON.stringify(result));
        return result;
    } catch (err) {
        console.warn('Error al obtener tamaños de letra de PDF:', err);
        return { ...DEFAULT_PDF_FONT_SIZES };
    }
};

/**
 * Obtiene todos los tamaños de fuente de título desde app_settings.
 */
export const getPdfTitleFontSizes = async () => {
    try {
        const { data, error } = await supabase
            .from('app_settings')
            .select('setting_key, setting_value')
            .in('setting_key', Object.values(PDF_TITLE_FONT_SIZE_KEYS));

        const result = { ...DEFAULT_PDF_TITLE_FONT_SIZES };

        try {
            const cached = localStorage.getItem('pdf_title_font_sizes');
            if (cached) {
                Object.assign(result, JSON.parse(cached));
            }
        } catch (e) {}

        if (!error && data) {
            data.forEach((item) => {
                const val = parseFloat(item.setting_value);
                if (!isNaN(val) && val > 0) {
                    for (const [t, key] of Object.entries(PDF_TITLE_FONT_SIZE_KEYS)) {
                        if (item.setting_key === key) {
                            result[t] = val;
                        }
                    }
                }
            });
        }

        localStorage.setItem('pdf_title_font_sizes', JSON.stringify(result));
        return result;
    } catch (err) {
        console.warn('Error al obtener tamaños de título de PDF:', err);
        return { ...DEFAULT_PDF_TITLE_FONT_SIZES };
    }
};

/**
 * Obtiene el tamaño de fuente para un tipo específico de cotización.
 */
export const getPdfFontSize = async (type) => {
    const key = PDF_FONT_SIZE_KEYS[type];
    if (!key) return DEFAULT_PDF_FONT_SIZES[type] || 8.5;

    try {
        const { data, error } = await supabase
            .from('app_settings')
            .select('setting_value')
            .eq('setting_key', key)
            .maybeSingle();

        if (!error && data?.setting_value) {
            const val = parseFloat(data.setting_value);
            if (!isNaN(val) && val > 0) {
                // Actualizar caché
                const current = { ...DEFAULT_PDF_FONT_SIZES };
                try {
                    const cached = localStorage.getItem('pdf_font_sizes');
                    if (cached) Object.assign(current, JSON.parse(cached));
                } catch (e) {}
                current[type] = val;
                localStorage.setItem('pdf_font_sizes', JSON.stringify(current));
                return val;
            }
        }
    } catch (err) {
        console.warn(`Error al consultar tamaño de letra para ${type}:`, err);
    }
    return getCachedPdfFontSize(type);
};

/**
 * Obtiene el tamaño de fuente de título para un tipo específico de cotización.
 */
export const getPdfTitleFontSize = async (type) => {
    const key = PDF_TITLE_FONT_SIZE_KEYS[type];
    if (!key) return DEFAULT_PDF_TITLE_FONT_SIZES[type] || 9;

    try {
        const { data, error } = await supabase
            .from('app_settings')
            .select('setting_value')
            .eq('setting_key', key)
            .maybeSingle();

        if (!error && data?.setting_value) {
            const val = parseFloat(data.setting_value);
            if (!isNaN(val) && val > 0) {
                const current = { ...DEFAULT_PDF_TITLE_FONT_SIZES };
                try {
                    const cached = localStorage.getItem('pdf_title_font_sizes');
                    if (cached) Object.assign(current, JSON.parse(cached));
                } catch (e) {}
                current[type] = val;
                localStorage.setItem('pdf_title_font_sizes', JSON.stringify(current));
                return val;
            }
        }
    } catch (err) {
        console.warn(`Error al consultar tamaño de título para ${type}:`, err);
    }
    return getCachedPdfTitleFontSize(type);
};

/**
 * Actualiza los tamaños de fuente de las cotizaciones en app_settings.
 */
export const updatePdfFontSizes = async (fontSizes) => {
    const validSizes = {
        packages: parseFloat(fontSizes?.packages) || DEFAULT_PDF_FONT_SIZES.packages,
        cruises: parseFloat(fontSizes?.cruises) || DEFAULT_PDF_FONT_SIZES.cruises,
        hotels: parseFloat(fontSizes?.hotels) || DEFAULT_PDF_FONT_SIZES.hotels,
        flights: parseFloat(fontSizes?.flights) || DEFAULT_PDF_FONT_SIZES.flights,
    };

    const upserts = Object.entries(validSizes).map(([type, size]) => ({
        setting_key: PDF_FONT_SIZE_KEYS[type],
        setting_value: size.toString(),
    }));

    const { error } = await supabase
        .from('app_settings')
        .upsert(upserts);

    if (error) {
        throw error;
    }

    localStorage.setItem('pdf_font_sizes', JSON.stringify(validSizes));
    return validSizes;
};

/**
 * Actualiza los tamaños de fuente de título en app_settings.
 */
export const updatePdfTitleFontSizes = async (titleFontSizes) => {
    const validSizes = {
        packages: parseFloat(titleFontSizes?.packages) || DEFAULT_PDF_TITLE_FONT_SIZES.packages,
        cruises: parseFloat(titleFontSizes?.cruises) || DEFAULT_PDF_TITLE_FONT_SIZES.cruises,
        hotels: parseFloat(titleFontSizes?.hotels) || DEFAULT_PDF_TITLE_FONT_SIZES.hotels,
        flights: parseFloat(titleFontSizes?.flights) || DEFAULT_PDF_TITLE_FONT_SIZES.flights,
    };

    const upserts = Object.entries(validSizes).map(([type, size]) => ({
        setting_key: PDF_TITLE_FONT_SIZE_KEYS[type],
        setting_value: size.toString(),
    }));

    const { error } = await supabase
        .from('app_settings')
        .upsert(upserts);

    if (error) {
        throw error;
    }

    localStorage.setItem('pdf_title_font_sizes', JSON.stringify(validSizes));
    return validSizes;
};

